import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail, Phone, Clock, ArrowRight, HelpCircle, FileText, Globe2, ChevronDown,
} from "lucide-react";
import { PageHero } from "./Services";
import { BRAND } from "@/data/content";
import Seo from "@/components/Seo";

const FAQS = [
  {
    q: "How quickly will I receive a response?",
    a: "We respond to every inquiry within two (2) business hours, Monday through Friday, 9:00 AM – 6:00 PM EST. Urgent requests submitted outside those hours receive a same-day reply on the next business day.",
  },
  {
    q: "How do I request a quote?",
    a: "Submit our Contact form with your source language, target language(s), document type, deadline, and any reference materials. Most quotes are returned within two business hours with a fixed price, turnaround, and clear scope — no obligation, no fine print.",
  },
  {
    q: "Do you handle confidential or regulated content?",
    a: "Yes. Legal, healthcare, financial, and government materials are routinely handled under NDA. Every linguist and project manager is bound by strict confidentiality protocols, and we can sign your MSA or BAA where required.",
  },
  {
    q: "Which languages do you support?",
    a: "85+ languages, with deep specialty in Caribbean Creoles (Haitian, Jamaican, Trinidadian), rare African languages, and minority-spoken Asian dialects — plus all standard European, Middle Eastern, and East Asian languages.",
  },
  {
    q: "Can you integrate with our localization workflow?",
    a: "Yes. We provide structured deliverables (XLIFF, TMX, SCORM, SRT, etc.), maintain client-specific glossaries and translation memories, and can integrate via API with your CMS, helpdesk, or product pipeline.",
  },
  {
    q: "What if I need certified or sworn translation?",
    a: "We provide certified translations accepted by USCIS, courts, embassies, universities, and regulatory bodies. Certification statements, notarization, and apostille services are available on request.",
  },
];

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${index}`} className="border-b border-[#E5E7EB]">
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-4 text-left group"
      >
        <span className="font-semibold text-[#071D49] text-[15px] leading-snug">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-[#5B6472] flex-shrink-0 transition-transform duration-300 group-hover:text-[#1A4E8A] mt-1 ${
            open ? "rotate-180 text-[#1A4E8A]" : ""
          }`}
        />
      </button>
      {open && <div className="pb-4 text-[#222222] leading-relaxed text-[14px]">{a}</div>}
    </div>
  );
}

export default function Support() {
  return (
    <div data-testid="support-page" className="bg-white">
      <Seo
        title="Support & Help Center"
        description="Quote help, response times, language scope, certified translations, and answers to common Petraloc support questions — all in one place."
        path="/support"
      />

      <PageHero
        eyebrow="Help Center"
        title="Support that speaks your language."
        subtitle="Whether you need a quote, a status update, or technical guidance on a localization project, our team is one message away."
      />

      {/* CONTACT GRID */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-4">
            <div data-testid="support-card-email" className="ent-card p-6">
              <Mail className="w-6 h-6 text-[#1A4E8A] mb-3" />
              <h3 className="font-semibold text-[#071D49] text-[15px] mb-1">Email support</h3>
              <p className="text-[13px] text-[#5B6472] mb-3">Fastest way to reach a human on our team.</p>
              <a href={`mailto:${BRAND.email}`} className="text-[#1A4E8A] font-semibold text-[13px] hover:text-[#071D49] inline-flex items-center gap-1.5">
                {BRAND.email} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

            <div data-testid="support-card-phone" className="ent-card p-6">
              <Phone className="w-6 h-6 text-[#1A4E8A] mb-3" />
              <h3 className="font-semibold text-[#071D49] text-[15px] mb-1">Direct line</h3>
              <p className="text-[13px] text-[#5B6472] mb-3">For complex projects or urgent requests.</p>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="text-[#1A4E8A] font-semibold text-[13px] hover:text-[#071D49] inline-flex items-center gap-1.5">
                {BRAND.phone} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

            <div data-testid="support-card-hours" className="ent-card p-6">
              <Clock className="w-6 h-6 text-[#1A4E8A] mb-3" />
              <h3 className="font-semibold text-[#071D49] text-[15px] mb-1">Business hours</h3>
              <p className="text-[13px] text-[#5B6472]">
                Monday – Friday<br />9:00 AM – 6:00 PM EST
              </p>
              <p className="mt-2 text-[12px] text-[#5B6472]">
                Response time: <span className="font-semibold text-[#071D49]">≤ 2 business hours</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE TIPS */}
      <section className="py-14 lg:py-16 bg-[#F7F8FA]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#00D1B2]">
              Quote request guidance
            </p>
            <h2 className="mt-3 text-3xl lg:text-[34px] font-semibold tracking-tight text-[#071D49] leading-[1.15]">
              The faster we get the details, the faster you get a price.
            </h2>
            <p className="mt-4 text-[14.5px] text-[#5B6472] leading-relaxed">
              Most quotes are returned within two business hours. To make that possible, share the following when you reach out:
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {[
              { icon: Globe2, t: "Language pair", d: "Source and target language(s), including regional variants (e.g., LATAM Spanish vs. Castilian)." },
              { icon: FileText, t: "Document type", d: "Legal contract, medical record, marketing copy, software string file, audio/video, etc." },
              { icon: Clock, t: "Deadline", d: "Your hard deadline and any preferred milestones — we'll confirm what's realistic." },
              { icon: HelpCircle, t: "Reference materials", d: "Glossaries, style guides, prior translations, or brand guidelines if available." },
            ].map((it) => (
              <div key={it.t} className="ent-card p-5" data-testid={`quote-tip-${it.t}`}>
                <it.icon className="w-5 h-5 text-[#1A4E8A] mb-3" />
                <h4 className="font-semibold text-[#071D49] text-[14px] mb-1">{it.t}</h4>
                <p className="text-[12.5px] text-[#5B6472] leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#1A4E8A] mb-3 text-center">
            Frequently asked questions
          </p>
          <h2 className="text-3xl lg:text-[34px] font-semibold tracking-tight text-[#071D49] leading-[1.15] text-center mb-10">
            Answers, before you even ask.
          </h2>
          <div data-testid="faq-list">
            {FAQS.map((f, i) => (
              <FaqItem key={f.q} index={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 bg-[#071D49] text-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <h2 className="text-2xl lg:text-[28px] font-semibold tracking-tight">
              Still have a question?
            </h2>
            <p className="mt-1.5 text-sm text-white/70">
              Reach our team — clear answer, next steps, within two business hours.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="support-final-cta"
            className="inline-flex items-center gap-2 bg-[#00D1B2] hover:bg-[#00A990] text-white px-6 py-3 rounded-md text-[13px] font-semibold w-fit transition-colors"
          >
            Contact our team <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
